import { Link, createFileRoute, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight } from 'lucide-react'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'
import { localizedPageHead } from '@/lib/seo-head'
import {
  LedgerCta,
  LedgerSection,
  LedgerSpecRow,
  LedgerStamp,
} from '@/components/ledger/LedgerBlocks'

export const Route = createFileRoute('/{-$locale}/cloud')({
  head: ({ params }) => localizedPageHead('cloud', params.locale),
  component: CloudLedgerPage,
})

const CATALOGUE = ['compute', 'data', 'messaging', 'storage', 'apps', 'inference'] as const
const PROOF = ['sovereign', 'managed', 'ai', 'agents'] as const

function CloudLedgerPage() {
  const { t } = useTranslation('ledger')
  const locale = getLocaleFromPath(useLocation().pathname)
  const href = (path: string) => withLocalePath(path, locale)

  return (
    <div>
      <header className="ledger-wrap pb-10 pt-12 md:pt-16">
        <div className="flex flex-wrap items-start gap-x-5 gap-y-3">
          <h1 className="ledger-display max-w-[24ch] text-[clamp(2.4rem,6vw,4.4rem)]">
            {t('cloud.title')}
          </h1>
          <span className="mt-2 shrink-0 md:mt-3">
            <LedgerStamp>{t('cloud.status')}</LedgerStamp>
          </span>
        </div>
        <p className="ledger-lead mt-6">{t('cloud.lead')}</p>
      </header>

      {/*
        Planned regions and an illustrative agent workflow. Availability and
        contract details remain explicitly unconfirmed.
      */}
      <section className="ledger-wrap pb-12">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="ledger-kicker mb-4">{t('cloud.sovereigntyTitle')}</p>
            <div className="ledger-block ledger-block--strong">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--rule)] px-5 py-3">
                <span className="ledger-figure text-[0.75rem] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {t('cloud.regionPlanLabel')}
                </span>
                <span className="ledger-figure text-[0.75rem] text-[var(--registry)]">
                  {t('cloud.availabilityLabel')}
                </span>
              </div>
              <div className="grid grid-cols-2">
                <div className="border-r border-[var(--rule)] px-5 py-6">
                  <div className="flex items-center gap-3">
                    <img
                      src="/flags/de.svg"
                      alt=""
                      aria-hidden="true"
                      className="ledger-flag"
                      width={26}
                      height={18}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="ledger-jurisdiction-code">
                      {t('cloud.regions.de.name')}
                    </div>
                  </div>
                  <p className="ledger-muted mt-3 text-[0.8125rem] leading-relaxed">
                    {t('cloud.regions.de.body')}
                  </p>
                </div>
                <div className="px-5 py-6">
                  <div className="flex items-center gap-3">
                    <img
                      src="/flags/ch.svg"
                      alt=""
                      aria-hidden="true"
                      className="ledger-flag"
                      width={26}
                      height={18}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="ledger-jurisdiction-code">
                      {t('cloud.regions.ch.name')}
                    </div>
                  </div>
                  <p className="ledger-muted mt-3 text-[0.8125rem] leading-relaxed">
                    {t('cloud.regions.ch.body')}
                  </p>
                </div>
              </div>
              <div className="border-t border-[var(--rule)] px-5 py-3">
                <p className="ledger-figure text-[0.75rem] text-[var(--ink-3)]">
                  {t('cloud.contractStatus')}
                </p>
              </div>
            </div>
            <p className="ledger-muted mt-4 text-[0.875rem] leading-relaxed">
              {t('cloud.sovereigntyIntro')}
            </p>
          </div>

          {/* Agent-native claim, shown as a real terminal exchange. */}
          <div className="lg:col-span-5">
            <p className="ledger-kicker mb-4">{t('cloud.agentTitle')}</p>
            <div className="ledger-block ledger-block--strong ledger-block--inset">
              <div className="flex items-center justify-between gap-2 border-b border-[var(--rule)] px-4 py-2.5">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[var(--stamp)]" />
                  <span className="ledger-figure text-[0.6875rem] text-[var(--ink-3)]">
                    mcp · horizon-cloud
                  </span>
                </span>
                <span className="ledger-figure text-[0.625rem] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {t('cloud.agentExampleLabel')}
                </span>
              </div>
              <pre className="overflow-x-auto px-4 py-4 text-[0.75rem] leading-relaxed" style={{ fontFamily: 'var(--ledger-mono)' }}>
                <code>
                  {`> deploy --service app --region de
  ✓ resource created (tenant: acme)
  ✓ container image pulled
  ✓ reachable at acme.horizon.cloud

> inspect tenant acme
  region        de
  jurisdiction  european
  databases     2 (managed, backed up)
  status        running`}
                </code>
              </pre>
            </div>
            <p className="ledger-muted mt-4 text-[0.875rem] leading-relaxed">
              {t('cloud.agentBody')}
            </p>
            <ul className="mt-4 space-y-2">
              {(['provision', 'deploy', 'open'] as const).map((key) => (
                <li
                  key={key}
                  className="flex items-start gap-2 text-[0.875rem] text-[var(--ink-2)]"
                >
                  <span
                    className="ledger-figure mt-0.5 text-[var(--stamp)]"
                    aria-hidden="true"
                  >
                    +
                  </span>
                  {t(`cloud.agentPoints.${key}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* The catalogue of what the platform runs. */}
      <LedgerSection title={t('cloud.catalogueTitle')} note={t('cloud.catalogueIntro')}>
        <dl className="ledger-spec">
          {CATALOGUE.map((key) => (
            <LedgerSpecRow key={key} term={t(`cloud.catalogue.${key}.name`)}>
              {t(`cloud.catalogue.${key}.body`)}
            </LedgerSpecRow>
          ))}
        </dl>
        <p className="ledger-muted mt-6 max-w-[68ch] text-[0.875rem] leading-relaxed">
          {t('cloud.catalogueNote')}
        </p>
      </LedgerSection>

      {/* Pricing, stated honestly as planned. */}
      <LedgerSection title={t('cloud.pricingTitle')}>
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="ledger-prose text-[1rem]">{t('cloud.pricingBody')}</p>
          </div>
          <div className="md:col-span-5">
            <div className="ledger-block grid grid-cols-3">
              {(['starter', 'business', 'sovereign'] as const).map((key, i) => (
                <div
                  key={key}
                  className={
                    'px-4 py-5 text-center' +
                    (i < 2 ? ' border-r border-[var(--rule)]' : '')
                  }
                >
                  <div className="ledger-figure text-[0.7rem] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    {t(`cloud.tiers.${key}`)}
                  </div>
                  <div className="ledger-figure mt-2 text-[1.1rem] text-[var(--ink)]">
                    —
                  </div>
                  <div className="ledger-muted mt-1 text-[0.7rem]">
                    {t('cloud.pricingStatus')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </LedgerSection>

      {/* The claims, each with its proof. */}
      <LedgerSection title={t('cloud.proofTitle')} note={t('cloud.proofIntro')}>
        <dl className="ledger-spec">
          {PROOF.map((key) => (
            <LedgerSpecRow key={key} term={t(`cloud.proof.${key}.claim`)}>
              {t(`cloud.proof.${key}.proof`)}
            </LedgerSpecRow>
          ))}
        </dl>
      </LedgerSection>

      {/* Research link-through. */}
      <LedgerSection title={t('cloud.researchTitle')}>
        <p className="ledger-prose text-[1rem]">{t('cloud.researchBody')}</p>
        <Link to={href('/research')} className="ledger-link mt-5 inline-flex">
          {t('cloud.researchLink')}
          <ArrowUpRight size={16} aria-hidden="true" />
        </Link>
      </LedgerSection>

      {/* Other products. */}
      <LedgerSection title={t('cloud.otherProducts')}>
        <div className="ledger-register">
          <Link
            to={href('/products/arkived')}
            className="ledger-register-row ledger-focus"
          >
            <span className="ledger-register-no">01</span>
            <h3 className="ledger-register-name">arkived</h3>
            <p className="ledger-register-desc">{t('home.arkived')}</p>
            <ArrowUpRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </LedgerSection>

      <LedgerCta
        topic="cloud-demo"
        title={t('cloud.ctaTitle')}
        body={t('cloud.ctaBody')}
        ctaLabel={t('cloud.ctaButton')}
      />
    </div>
  )
}
